/**
 * Symmetric card-combat DOM overlay (ADR-0015 §12; supersedes the four-band
 * ADR-0011 §2 layout). Retains the DOM-overlay-above-Pixi pattern (ADR-0011 §1)
 * and the HEX-32 mount/update/destroy lifecycle.
 *
 * Layout (top → bottom): enemy band (name + tier, HP + block + statuses, dice
 * pool, full loadout grid) · log band · player band (same structure) with the
 * end-turn button bottom-right.
 *
 * Symmetry / visibility (ADR-0015 §4, §11.2): both loadout grids and both status
 * bags are visible at all times; the *dice pool* of a side is shown only on that
 * side's turn — `state.pool` only ever holds the active side's dice.
 *
 * Engine seam: reads `CardCombatState`, emits `CardEngineIntent`
 * (src/combat/cardEngine.ts — the §11 turn-by-turn engine).
 * Two ways to drive it:
 *   - `mount(container, { state, onIntent })` — host owns the engine loop; the
 *     overlay emits intents. Used by tests to inspect emitted intents.
 *   - `open(container, encounter, driver, onOutcome)` — the overlay owns the
 *     loop: applies player intents through the injected driver, auto-runs enemy
 *     turns, and reports `'victory' | 'defeat' | 'forfeit'` (ADR-0011 §9).
 *
 * Inputs (ADR-0011 §3): drag a die onto a player loadout card to assign it;
 * click-then-click fallback (click a die, then a card). Click an assigned card
 * to clear it. Click a free card (Reroll) to activate it. Esc opens the §19
 * retreat-roll prompt (odd slips away, even forfeits the next turn).
 */

import { TweenRegistry, easeOutQuad } from '../../animation/tween';
import { createGlyphElement } from '../../render/glyphs';
import type { CardCombatState, CardEngineIntent, CombatEvent, DieSlot, Side } from '../cardEngine';
import { isDieCard } from '../cardEngine';
import { PLAIN_D6 } from '../dice';
import { choosePlayerAssignment } from '../enemyAi';
import { matches } from '../predicates';
import type {
  AiTier,
  Card,
  CardId,
  Combatant,
  DieFace,
  GridPlacement,
  Predicate,
  Rarity,
  StatusApplication,
} from '../types';
import { RARITY_COLOUR_VAR, renderCardFace } from './cardFace';
import {
  CardHoverPanel,
  buildLegend,
  costBonusBadge,
  extraCostBadge,
  scaledPreview,
} from './cardHoverPanel';
import { type SideNamer, formatEvent } from './combatLog';
import { dieCardLink } from './dieAffordance';
import { facePips, faceRestPose, tumbleStartPose } from './dieGeometry';
import { renderGridDieChip } from './dieTray';
import { CombatJuice } from './juice';

export type CombatOutcome = 'victory' | 'defeat' | 'forfeit';

/** The combat-start payload (ADR-0015 §9). Tier drives the enemy band label. */
export interface Encounter {
  player: Combatant;
  enemy: Combatant;
  seed: number;
  enemyTier?: Rarity;
  /** The enemy's identity glyph (HEX-69 / `EnemyTemplate.glyph`), shown in the
   * enemy band so the foe reads at a glance. Omitted in bare-state mounts. */
  enemyGlyph?: string;
}

/** The slice of the engine the overlay needs when it owns the loop. Injected so
 * the overlay's tests can stub `applyIntent` without instantiating combat. */
export interface EngineDriver {
  createCombat(args: { player: Combatant; enemy: Combatant; seed: number }): CardCombatState;
  applyIntent(state: CardCombatState, intent: CardEngineIntent): CardCombatState;
}

export interface CardCombatOverlayMountOptions {
  state: CardCombatState;
  onIntent: (intent: CardEngineIntent) => void;
  enemyTier?: Rarity;
  enemyGlyph?: string;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

const DRAG_MIME = 'application/x-hex6-carddie';
const DATA_DIE_INDEX = 'data-die-index';
const DATA_CARD_ID = 'data-card-id';

const REJECT_CLASS = 'hex6-card-combat__die--reject';
const REJECT_MS = 240;
/** HEX-88 — how long the frozen-card drop-rejection reason toast lingers. */
const REJECT_REASON_MS = 1800;
/** Dice-tumble timing (ADR-0017 §1). Duration + per-die stagger live in CSS;
 * mirrored here only to bound the will-change cleanup if `transitionend` never
 * fires (reduced-motion, detached node, or a synchronous re-render mid-tumble). */
const TUMBLE_MS = 620;
const TUMBLE_STAGGER_MS = 60;
const TUMBLE_CLASS = 'hex6-card-combat__cube--tumbling';
/** Delay before the overlay auto-runs the enemy turn — lets the player read the
 * enemy roll before the bot starts acting (ADR-0015 §11.2). */
const ENEMY_TURN_DELAY_MS = 650;
/** Per-event delay while the enemy turn is narrated (HEX-69). The bot resolves in
 * one engine call, so the overlay replays its new log events one at a time at
 * this cadence — each assignment / resolution / trap fire becomes watchable, with
 * HP draining in step. Tunable; a skip affordance fast-forwards to the end. */
const ENEMY_STEP_MS = 520;
/** HEX-148 — auto-play cadence. Reuses the HEX-69 enemy-narration beat so an
 * auto-played player turn reads at the same watchable pace (one die per beat).
 * Reduced motion is honoured by the render path (no tumble), not by the cadence —
 * the step delay is pacing, matching the enemy narration which keeps it too. */
const AUTO_STEP_MS = ENEMY_STEP_MS;
/** HEX-148 — the tier the AI plays the player's turns at: legendary = full board
 * value + player-trap awareness (ADR-0016 §4), so Auto never throws a winnable
 * fight. The player's own (absent) `aiProfile` is overridden per turn. */
const AUTO_PLAYER_TIER: AiTier = 'legendary';
/** Dice-fly-to-slot timing (ADR-0021 §2). A rolled enemy die flies from the dice
 * tray into the card slot it feeds; comfortably inside one `ENEMY_STEP_MS` beat so
 * the card has landed its dice before the next event (the hit) is narrated. The
 * duration mirrors the CSS transition; `+80` backstops a dropped `transitionend`. */
const FLY_MS = 360;
const FLY_CLASS = 'hex6-card-combat__flight-die';
const HP_TWEEN_MS = 260;
/** How long the clean-retreat message lingers before the overlay forfeits. */
const RETREAT_EXIT_MS = 900;

const STATUS_ICON: Record<string, string> = { poison: '☠', weaken: '▼' };

const DIE_GLYPHS: Record<number, string> = {
  1: '⚀',
  2: '⚁',
  3: '⚂',
  4: '⚃',
  5: '⚄',
  6: '⚅',
};

/** A played enemy card's filled die-slots for a paced render (ADR-0021 §2):
 * `slots` maps a cost-slot index to the die now in it; `extras` are the §19
 * scaling dice in the overflow row. Each carries the rolled face + its pool index
 * (the index addresses the flying-die source in the tray). */
interface EnemyCardFill {
  slots: Map<number, { dieIndex: number; face: DieFace }>;
  extras: Array<{ dieIndex: number; face: DieFace }>;
}

export class CardCombatOverlay {
  private root: HTMLDivElement | null = null;
  private state: CardCombatState | null = null;
  private onIntent: ((intent: CardEngineIntent) => void) | null = null;
  private enemyTier: Rarity | undefined;
  private enemyGlyph: string | undefined;
  private selectedDieIndex: number | null = null;
  /** `${turn}:${activeSide}` of the last roll whose dice tumbled in. Dice tumble
   * once on roll-phase entry; re-renders within the same turn (assign / reject)
   * show them at rest. Null until the first roll shows. */
  private lastTumbleKey: string | null = null;
  private tumbleCleanupTimers = new Map<HTMLElement, number>();

  // Internal-loop mode (open) — null when the host drives via onIntent.
  private driver: EngineDriver | null = null;
  private onOutcome: ((outcome: CombatOutcome) => void) | null = null;
  private enemyTurnTimer: number | null = null;
  private outcomeReported = false;

  // HEX-148 auto-combat — while `auto` is on the AI plays each player turn through
  // the normal assignDie/endTurn intents on the AUTO_STEP_MS beat (so the player
  // sees it). `autoPlan` is the queued assignment steps for the current turn,
  // computed once at turn start via `choosePlayerAssignment`; `autoTimer` paces
  // them. Toggling off cancels both and returns manual control. Open-loop only.
  private auto = false;
  private autoButton: HTMLButtonElement | null = null;
  private autoTimer: number | null = null;
  private autoPlan: Array<{ dieIndex: number; cardId: CardId }> | null = null;

  // Enemy-turn narration (HEX-69). The bot resolves in one engine call; we hold
  // the final state and reveal its new log tail one event at a time so the turn
  // is watchable. Null when no enemy turn is playing back.
  private playback: {
    finalState: CardCombatState;
    events: CombatEvent[];
    baseLogLength: number;
    revealed: number;
    baselineHp: { player: number; enemy: number };
    // ADR-0021 §2 — the enemy turn's reconstructed die→card feed, animated onto the
    // paced replay: `pool` is the enemy's rolled dice, `plays` the per-card feed (in
    // `cardPlayed` order), `playEventIndex[p]` the event whose reveal fires play p.
    enemyAnim: {
      pool: Array<{ index: number; face: DieFace }>;
      plays: EnemyPlay[];
      playEventIndex: number[];
    };
  } | null = null;
  private playbackTimer: number | null = null;

  // ADR-0021 §2 — the enemy-turn animation view for the current paced render:
  // which rolled dice still sit in the tray, and each played card's filled slots.
  // Null on every non-paced render (the player owns the board then).
  private enemyView: {
    trayDice: Array<{ index: number; face: DieFace }>;
    fills: Map<string, EnemyCardFill>;
    tumble: boolean;
  } | null = null;
  // Dice-flight layer (ADR-0021 §2) — transient flying cubes mount here, above the
  // bands; survives the per-step band re-render (renderState only swaps the mounts).
  private flightLayer: HTMLDivElement | null = null;
  private flightNodeTimers = new Map<HTMLElement, number>();

  // Stable structural nodes built once by mount.
  private headerTurn: HTMLSpanElement | null = null;
  private headerActive: HTMLSpanElement | null = null;
  private skipButton: HTMLButtonElement | null = null;
  private enemyMount: HTMLDivElement | null = null;
  private trayMount: HTMLDivElement | null = null;
  private logMount: HTMLDivElement | null = null;
  private playerMount: HTMLDivElement | null = null;
  private endTurnButton: HTMLButtonElement | null = null;
  private verdict: HTMLSpanElement | null = null;

  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private rejectTimers = new Map<HTMLElement, number>();
  private readonly hoverPanel = new CardHoverPanel();

  // §19 retreat prompt + transient outcome toast.
  private retreatPrompt: HTMLDivElement | null = null;
  private retreatToast: HTMLDivElement | null = null;
  private retreatExitTimer: number | null = null;

  // HEX-88 transient drop-rejection reason toast (e.g. frozen card).
  private rejectReasonToast: HTMLDivElement | null = null;
  private rejectReasonTimer: number | null = null;

  // Impact-feedback dispatcher (ADR-0017 §B). `juiceCursor` is how far through
  // the displayed log we have fired moments; events past it (live resolution or a
  // newly-revealed paced step) get flushed through `playJuice` after each render.
  private juice: CombatJuice | null = null;
  private juiceCursor = 0;

  // HP-bar tweens (HEX-46 primitive) advanced off a single rAF loop.
  private readonly tweens = new TweenRegistry();
  private rafHandle: number | null = null;
  private lastFrameMs = 0;
  private prevHp = new Map<Side, number>();

  // ── lifecycle ─────────────────────────────────────────────────────────

  mount(container: HTMLElement, options: CardCombatOverlayMountOptions): void {
    if (this.root) return;
    this.onIntent = options.onIntent;
    this.enemyTier = options.enemyTier;
    this.enemyGlyph = options.enemyGlyph;

    const root = document.createElement('div');
    root.className = 'hex6-card-combat';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Combat');

    // Arena floor light — a warm pool rising from below (ADR-0020 §4a "lit
    // arena"). Decorative, static, never intercepts input.
    const floor = document.createElement('div');
    floor.className = 'hex6-card-combat__floor';
    floor.setAttribute('aria-hidden', 'true');

    const header = document.createElement('div');
    header.className = 'hex6-card-combat__header';
    const title = document.createElement('span');
    title.className = 'hex6-card-combat__title';
    title.textContent = 'Combat';
    this.headerTurn = document.createElement('span');
    this.headerTurn.className = 'hex6-card-combat__turn';
    this.headerActive = document.createElement('span');
    this.headerActive.className = 'hex6-card-combat__active';
    const forfeit = document.createElement('span');
    forfeit.className = 'hex6-card-combat__forfeit';
    forfeit.textContent = 'esc · retreat ⚄';
    // Skip / fast-forward the enemy narration (HEX-69) — hidden until a bot turn
    // plays back. Lives in the header so it never shifts the band layout.
    this.skipButton = document.createElement('button');
    this.skipButton.type = 'button';
    this.skipButton.className = 'hex6-card-combat__skip';
    this.skipButton.dataset.action = 'skip-enemy-turn';
    this.skipButton.textContent = 'Skip ⏭';
    this.skipButton.hidden = true;
    this.skipButton.addEventListener('click', () => this.skipEnemyPlayback());
    header.append(title, this.headerTurn, this.headerActive, this.skipButton, forfeit);

    // Enemy zone — looming at the top (ADR-0020 §4a). Medallion + vitals + the
    // foe loadout (its visible intent, ADR-0015 §10 — no telegraph).
    const enemyBand = document.createElement('section');
    enemyBand.className = 'hex6-card-combat__band hex6-card-combat__band--enemy';
    this.enemyMount = document.createElement('div');
    this.enemyMount.className = 'hex6-card-combat__mount';
    enemyBand.appendChild(this.enemyMount);

    // Centre dice tray — the tactile heart (ADR-0020 §4a). Holds the active
    // side's rolled pool; the hint above it carries the assign affordance.
    const tray = document.createElement('section');
    tray.className = 'hex6-card-combat__tray';
    this.trayMount = document.createElement('div');
    this.trayMount.className = 'hex6-card-combat__mount';
    tray.appendChild(this.trayMount);

    this.logMount = document.createElement('div');
    this.logMount.className = 'hex6-card-combat__log';
    this.logMount.setAttribute('aria-label', 'Combat log');

    // Player zone — the lit hero at the bottom (ADR-0020 §4a).
    const playerBand = document.createElement('section');
    playerBand.className = 'hex6-card-combat__band hex6-card-combat__band--player';
    this.playerMount = document.createElement('div');
    this.playerMount.className = 'hex6-card-combat__mount';
    playerBand.appendChild(this.playerMount);

    // Right rail (footer): type legend stacked over the verdict + the primary
    // End Turn (ADR-0020 §4a end-turn/legend cluster).
    const footer = document.createElement('div');
    footer.className = 'hex6-card-combat__footer';
    const legend = buildLegend();
    legend.classList.add('hex6-card-combat__legend');
    this.verdict = document.createElement('span');
    this.verdict.className = 'hex6-card-combat__verdict';
    this.verdict.hidden = true;
    // HEX-148 — Auto toggle: hands the player's turns to the AI (legendary tier).
    // Lives beside End Turn; stays enabled across the whole fight so the player can
    // arm or disarm it at any point, including mid enemy-narration.
    this.autoButton = document.createElement('button');
    this.autoButton.type = 'button';
    this.autoButton.className = 'hex6-card-combat__auto';
    this.autoButton.dataset.action = 'toggle-auto';
    this.autoButton.addEventListener('click', () => this.toggleAuto());
    this.endTurnButton = document.createElement('button');
    this.endTurnButton.type = 'button';
    this.endTurnButton.className = 'hex6-card-combat__end-turn';
    this.endTurnButton.textContent = 'End Turn ▸';
    this.endTurnButton.dataset.action = 'end-turn';
    this.endTurnButton.addEventListener('click', () => this.handleEndTurn());
    footer.append(legend, this.verdict, this.autoButton, this.endTurnButton);
    this.syncAutoButton();
    playerBand.appendChild(footer);

    // Depth chrome (ADR-0020 §1) — vignette + grain over the whole arena. Below
    // the juice layer (z-index 36); never intercept input.
    const vignette = document.createElement('div');
    vignette.className = 'hex6-card-combat__vignette';
    vignette.setAttribute('aria-hidden', 'true');
    const grain = document.createElement('div');
    grain.className = 'hex6-card-combat__grain';
    grain.setAttribute('aria-hidden', 'true');

    root.append(floor, header, enemyBand, tray, this.logMount, playerBand, vignette, grain);
    container.appendChild(root);
    this.root = root;

    // Dice-flight layer (ADR-0021 §2) — flying enemy cubes mount here, above the
    // bands but below the juice floaters. Decorative; never intercepts input.
    const flight = document.createElement('div');
    flight.className = 'hex6-card-combat__flight';
    flight.setAttribute('aria-hidden', 'true');
    root.appendChild(flight);
    this.flightLayer = flight;

    // Impact-feedback layer (ADR-0017 §B). Mounted above the bands; floaters and
    // pulses target the freshly-rendered combatant cards via `cardOf`.
    this.juice = new CombatJuice({
      root,
      cardOf: (side) =>
        this.root?.querySelector<HTMLElement>(`.hex6-card-combat__combatant--${side}`) ?? null,
      reducedMotion: prefersReducedMotion(),
    });

    this.keyHandler = (e) => this.onKey(e);
    document.addEventListener('keydown', this.keyHandler);

    this.startFrameLoop();
    this.update(options.state);
  }

  /**
   * Drive an entire encounter, owning the engine loop (ADR-0011 §9). Mounts the
   * overlay, applies player intents through `driver`, auto-runs enemy turns, and
   * calls `onOutcome` once when combat ends or the player forfeits.
   */
  open(
    container: HTMLElement,
    encounter: Encounter,
    driver: EngineDriver,
    onOutcome: (outcome: CombatOutcome) => void,
  ): void {
    this.driver = driver;
    this.onOutcome = onOutcome;
    this.outcomeReported = false;
    const initial = driver.createCombat({
      player: encounter.player,
      enemy: encounter.enemy,
      seed: encounter.seed,
    });
    this.mount(container, {
      state: initial,
      onIntent: (intent) => this.driveIntent(intent),
      ...(encounter.enemyTier ? { enemyTier: encounter.enemyTier } : {}),
      ...(encounter.enemyGlyph ? { enemyGlyph: encounter.enemyGlyph } : {}),
    });
    this.maybeScheduleEnemyTurn();
  }

  update(state: CardCombatState): void {
    if (!this.root) return;
    this.state = state;
    if (this.selectedDieIndex !== null) {
      const slot = state.pool[this.selectedDieIndex];
      if (slot === undefined || slot.assignedTo !== null) this.selectedDieIndex = null;
    }
    this.renderState(state, false);
  }

  /**
   * Paint the overlay from `state`. `paced` is true only while an enemy turn is
   * being narrated (HEX-69): the player's pool is suppressed (the live pool already
   * belongs to the player's next turn), the end-turn button is held, and the skip
   * affordance shows. The enemy's rolled pool is instead surfaced from `enemyView`
   * (ADR-0021 §2) — its dice tumble into the tray and fly into the card slots they
   * feed, so `renderTray` shows the enemy pool here, not nothing.
   */
  private renderState(state: CardCombatState, paced: boolean): void {
    if (!this.root) return;
    // The enemy-turn animation view is only valid mid-playback; a normal render
    // (player's board) owns no flying dice (renderPlayback re-sets it each step).
    if (!paced) this.enemyView = null;

    if (this.headerTurn) this.headerTurn.textContent = `Combat — turn ${state.turn}`;
    if (this.headerActive) {
      this.headerActive.textContent =
        state.phase === 'ended'
          ? 'ended'
          : state.activeSide === 'player'
            ? 'your turn'
            : 'enemy turn';
    }
    if (this.skipButton) this.skipButton.hidden = !paced;

    // Tumble the dice once per roll-phase entry. The active side's pool is the
    // only one shown; keying off `turn:activeSide` means assign / reject
    // re-renders within a turn paint the dice at rest, not re-tumbling (§1).
    const rollKey = `${state.turn}:${state.activeSide}`;
    const fresh = !paced && state.phase !== 'ended' && rollKey !== this.lastTumbleKey;
    if (fresh) this.lastTumbleKey = rollKey;
    const tumble = fresh && !prefersReducedMotion();

    // Active-turn spotlight (ADR-0020 §4a): the side whose turn it is glows.
    const enemyActive = state.phase !== 'ended' && state.activeSide === 'enemy';
    const playerActive = state.phase !== 'ended' && state.activeSide === 'player';
    if (this.enemyMount) {
      this.enemyMount.replaceChildren(
        this.renderCombatant(
          state,
          'enemy',
          state.enemy,
          enemyActive,
          this.enemyTier,
          this.enemyView?.fills,
        ),
      );
    }
    if (this.trayMount) {
      this.trayMount.replaceChildren(this.renderTray(state, paced, tumble));
    }
    if (this.playerMount) {
      this.playerMount.replaceChildren(
        this.renderCombatant(state, 'player', state.player, playerActive),
      );
    }
    // Kick the player roll-phase tumble (`tumble`) or the enemy's opening tray
    // tumble (ADR-0021 §2) — both seed `data-tumble="pending"` cubes for kickoff.
    if (tumble || this.enemyView?.tumble) this.kickTumbles();
    this.renderLog(state);
    this.animateHpBars(state);
    // Fire impact feedback for events revealed since the last render (live
    // resolution appends to the log; a paced step reveals one more). Runs after
    // the cards are mounted so the moments target the fresh nodes (ADR-0017 §B).
    this.flushJuice(state);

    if (this.endTurnButton) {
      this.endTurnButton.disabled =
        paced || !(state.phase === 'assign' && state.activeSide === 'player');
    }
    if (this.verdict) {
      if (state.phase === 'ended' && state.victor !== null) {
        this.verdict.hidden = false;
        this.verdict.textContent = state.victor === 'player' ? 'Victory' : 'Defeat';
        this.verdict.classList.toggle(
          'hex6-card-combat__verdict--victory',
          state.victor === 'player',
        );
        this.verdict.classList.toggle(
          'hex6-card-combat__verdict--defeat',
          state.victor === 'enemy',
        );
      } else {
        this.verdict.hidden = true;
      }
    }
  }

  destroy(): void {
    if (this.enemyTurnTimer !== null) {
      clearTimeout(this.enemyTurnTimer);
      this.enemyTurnTimer = null;
    }
    if (this.playbackTimer !== null) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }
    if (this.autoTimer !== null) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
    this.autoPlan = null;
    this.auto = false;
    this.playback = null;
    this.clearFlights();
    this.flightLayer = null;
    this.enemyView = null;
    if (this.retreatExitTimer !== null) {
      clearTimeout(this.retreatExitTimer);
      this.retreatExitTimer = null;
    }
    this.dismissRetreatPrompt();
    this.retreatToast?.remove();
    this.retreatToast = null;
    if (this.rejectReasonTimer !== null) {
      clearTimeout(this.rejectReasonTimer);
      this.rejectReasonTimer = null;
    }
    this.rejectReasonToast?.remove();
    this.rejectReasonToast = null;
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.tweens.cancelAll();
    this.hoverPanel.destroy();
    for (const timer of this.rejectTimers.values()) clearTimeout(timer);
    this.rejectTimers.clear();
    for (const timer of this.tumbleCleanupTimers.values()) clearTimeout(timer);
    this.tumbleCleanupTimers.clear();
    this.lastTumbleKey = null;
    this.juice?.destroy();
    this.juice = null;
    this.juiceCursor = 0;
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    this.root?.remove();
    this.root = null;
    this.state = null;
    this.onIntent = null;
    this.driver = null;
    this.onOutcome = null;
    this.selectedDieIndex = null;
    this.prevHp.clear();
    this.headerTurn = null;
    this.headerActive = null;
    this.skipButton = null;
    this.enemyMount = null;
    this.trayMount = null;
    this.logMount = null;
    this.playerMount = null;
    this.endTurnButton = null;
    this.autoButton = null;
    this.verdict = null;
  }

  // ── internal loop (open mode) ───────────────────────────────────────────

  private driveIntent(intent: CardEngineIntent): void {
    if (!this.driver || !this.state) return;
    // The enemy turn is locked out of player input — drop intents while it plays.
    if (this.playback) return;
    const prev = this.state;
    let next: CardCombatState;
    try {
      next = this.driver.applyIntent(prev, intent);
    } catch {
      // Engine rejected (e.g. die satisfies no open cost slot) — shake the die
      // if we can identify it, otherwise no-op.
      if (intent.kind === 'assignDie') this.rejectDie(intent.dieIndex);
      return;
    }
    // The whole enemy turn resolves in this one call (auto-assign + resolve +
    // hand-back). Narrate it event-by-event instead of jumping (HEX-69).
    if (intent.kind === 'runEnemyTurn') {
      this.beginEnemyPlayback(prev, next);
      return;
    }
    this.update(next);
    // §19 clean retreat: linger on the outcome message, then forfeit.
    if (next.retreat === 'clean') {
      this.showRetreatToast('You slipped away — retreat clean');
      this.retreatExitTimer = setTimeout(
        () => this.reportOutcome('forfeit'),
        RETREAT_EXIT_MS,
      ) as unknown as number;
      return;
    }
    if (intent.kind === 'attemptRetreat' && next.skipNextPlayerTurn) {
      this.showRetreatToast('Retreat failed — you forfeit your next turn');
    }
    if (next.phase === 'ended') {
      this.reportOutcome(next.victor === 'player' ? 'victory' : 'defeat');
      return;
    }
    this.maybeScheduleEnemyTurn();
  }

  private maybeScheduleEnemyTurn(): void {
    if (!this.driver || !this.state) return;
    if (this.state.phase !== 'assign' || this.state.activeSide !== 'enemy') return;
    if (this.enemyTurnTimer !== null) return;
    this.enemyTurnTimer = setTimeout(() => {
      this.enemyTurnTimer = null;
      this.driveIntent({ kind: 'runEnemyTurn' });
    }, ENEMY_TURN_DELAY_MS) as unknown as number;
  }

  private reportOutcome(outcome: CombatOutcome): void {
    if (this.outcomeReported) return;
    this.outcomeReported = true;
    this.onOutcome?.(outcome);
  }

  // ── auto-combat (HEX-148) ───────────────────────────────────────────────────

  /** Toggle the AI playing the player's turns. Turning on kicks off the current
   * player turn (if any); turning off cancels the in-flight plan and returns
   * manual control — already-placed dice stay put, the player resumes by hand. */
  private toggleAuto(): void {
    this.auto = !this.auto;
    this.syncAutoButton();
    if (this.auto) this.maybeStartAutoPlayerTurn();
    else this.cancelAutoPlan();
  }

  private syncAutoButton(): void {
    const b = this.autoButton;
    if (!b) return;
    b.textContent = this.auto ? 'Auto ●' : 'Auto ○';
    b.setAttribute('aria-pressed', String(this.auto));
    b.classList.toggle('hex6-card-combat__auto--on', this.auto);
  }

  private cancelAutoPlan(): void {
    if (this.autoTimer !== null) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
    this.autoPlan = null;
  }

  /** Begin (or no-op) an auto-played player turn. Guarded so it only fires on a
   * live player assign-phase with no narration / retreat prompt in flight and no
   * plan already running. The plan is computed once from the current state via
   * `choosePlayerAssignment` (the symmetric selector, ADR-0016) and the queue then
   * drives the per-beat assigns; only open-loop combat (a driver present) auto-plays. */
  private maybeStartAutoPlayerTurn(): void {
    if (!this.auto || !this.driver || !this.state) return;
    if (this.playback || this.retreatPrompt) return;
    if (this.autoPlan !== null || this.autoTimer !== null) return;
    const state = this.state;
    if (state.phase !== 'assign' || state.activeSide !== 'player') return;
    const plan: Array<{ dieIndex: number; cardId: CardId }> = [];
    for (const a of choosePlayerAssignment(state, AUTO_PLAYER_TIER)) {
      for (const dieIndex of a.dieIndices) plan.push({ dieIndex, cardId: a.cardId });
    }
    this.autoPlan = plan;
    this.scheduleAutoStep();
  }

  private scheduleAutoStep(): void {
    if (this.autoPlan === null) return;
    this.autoTimer = setTimeout(() => {
      this.autoTimer = null;
      this.autoStep();
    }, AUTO_STEP_MS) as unknown as number;
  }

  /** Emit one queued assign, or close the turn once the plan is exhausted. Bails
   * if Auto was toggled off or the turn is no longer ours (death / hand-off
   * mid-plan), so a loss resolves normally — no special-casing hides it. */
  private autoStep(): void {
    if (!this.auto || this.autoPlan === null || !this.state) {
      this.autoPlan = null;
      return;
    }
    if (this.state.phase !== 'assign' || this.state.activeSide !== 'player') {
      this.autoPlan = null;
      return;
    }
    const next = this.autoPlan.shift();
    if (next === undefined) {
      // Plan exhausted — close the turn exactly as the End Turn button would.
      this.autoPlan = null;
      this.driveIntent({ kind: 'endTurn' });
      return;
    }
    this.driveIntent({ kind: 'assignDie', dieIndex: next.dieIndex, cardId: next.cardId });
    // The assign may have ended combat or handed to the enemy mid-plan; keep
    // stepping only while it is still our assign phase.
    if (this.state?.phase === 'assign' && this.state.activeSide === 'player') {
      this.scheduleAutoStep();
    } else {
      this.autoPlan = null;
    }
  }

  // ── enemy-turn narration (HEX-69) ───────────────────────────────────────────

  /** Replay the enemy turn's new log tail one event at a time so the bot's turn
   * is watchable. `prev` is the pre-resolution state (enemy-turn-start HP is the
   * playback baseline); `next` is the fully-resolved state we commit at the end. */
  private beginEnemyPlayback(prev: CardCombatState, next: CardCombatState): void {
    const baseLogLength = prev.log.length;
    const events = next.log.slice(baseLogLength);
    if (events.length === 0) {
      this.finishEnemyPlayback(next);
      return;
    }
    this.playback = {
      finalState: next,
      events,
      baseLogLength,
      revealed: 0,
      baselineHp: { player: prev.player.hp, enemy: prev.enemy.hp },
      enemyAnim: buildEnemyAnim(prev, next, events),
    };
    this.renderPlayback();
    this.scheduleEnemyStep();
  }

  private scheduleEnemyStep(): void {
    if (!this.playback) return;
    this.playbackTimer = setTimeout(() => {
      this.playbackTimer = null;
      if (!this.playback) return;
      // Capture the enemy dice positions BEFORE the re-render swaps the tray out:
      // the dice this step's played card consumes fly from where they sit now.
      const sources = this.captureEnemyTrayRects();
      this.playback.revealed += 1;
      this.renderPlayback();
      const justRevealed = this.playback.events[this.playback.revealed - 1];
      if (justRevealed?.kind === 'cardPlayed' && justRevealed.side === 'enemy') {
        this.flyEnemyDiceForCard(justRevealed.cardId, sources);
      }
      if (this.playback.revealed >= this.playback.events.length) {
        const final = this.playback.finalState;
        this.playback = null;
        this.finishEnemyPlayback(final);
      } else {
        this.scheduleEnemyStep();
      }
    }, ENEMY_STEP_MS) as unknown as number;
  }

  /** Fast-forward: drop the remaining steps and commit the resolved state now. */
  private skipEnemyPlayback(): void {
    if (!this.playback) return;
    if (this.playbackTimer !== null) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }
    const final = this.playback.finalState;
    this.playback = null;
    this.clearFlights();
    // Fast-forward: the skipped beats are not replayed, so don't juice them —
    // jump the cursor to the end before committing the resolved state.
    this.juiceCursor = final.log.length;
    this.finishEnemyPlayback(final);
  }

  private finishEnemyPlayback(final: CardCombatState): void {
    this.update(final);
    if (final.phase === 'ended') {
      this.reportOutcome(final.victor === 'player' ? 'victory' : 'defeat');
      return;
    }
    // A failed retreat (§19) forfeits the player's next turn: `runEnemyTurn`
    // auto-consumes that skipped turn and hands straight back to the enemy
    // (cardTuning.test.ts §"auto-skipped"). Reschedule so the bot keeps going —
    // `maybeScheduleEnemyTurn` no-ops when the hand-back is the player's turn.
    this.maybeScheduleEnemyTurn();
    // HEX-148 — if Auto is armed and the hand-back is to the player, play it.
    // The two are mutually exclusive (one no-ops on the side the other handles).
    this.maybeStartAutoPlayerTurn();
  }

  private renderPlayback(): void {
    if (!this.root || !this.playback) return;
    this.enemyView = this.computeEnemyView();
    this.renderState(this.buildPlaybackState(), true);
  }

  /** The enemy-turn animation view for the current reveal point (ADR-0021 §2): the
   * rolled dice still waiting in the tray, and each already-played card's filled
   * slots. A play's dice appear once its `cardPlayed` event has been revealed; the
   * tray holds whatever is not yet consumed. Tumble only on the opening render. */
  private computeEnemyView(): CardCombatOverlay['enemyView'] {
    const pb = this.playback;
    if (!pb) return null;
    const anim = pb.enemyAnim;
    const faceOf = new Map(anim.pool.map((d) => [d.index, d.face] as const));
    const consumed = new Set<number>();
    const fills = new Map<string, EnemyCardFill>();
    anim.plays.forEach((play, p) => {
      if ((anim.playEventIndex[p] ?? Number.POSITIVE_INFINITY) >= pb.revealed) return;
      const slots = new Map<number, { dieIndex: number; face: DieFace }>();
      play.slots.forEach((dieIndex, slotIndex) => {
        if (dieIndex === null) return;
        consumed.add(dieIndex);
        slots.set(slotIndex, { dieIndex, face: faceOf.get(dieIndex) ?? 1 });
      });
      const extras = play.extras.map((dieIndex) => {
        consumed.add(dieIndex);
        return { dieIndex, face: faceOf.get(dieIndex) ?? 1 };
      });
      fills.set(play.cardId, { slots, extras });
    });
    const trayDice = anim.pool.filter((d) => !consumed.has(d.index));
    return { trayDice, fills, tumble: pb.revealed === 0 && !prefersReducedMotion() };
  }

  /** Fire impact feedback for every log event not yet juiced, up to the displayed
   * log length. Unifies the two callers (ADR-0017 §3): live resolution renders
   * the full log; a paced step renders one more revealed event — either way only
   * the new tail fires. The cursor tracks full-log indices, shared across both. */
  private flushJuice(state: CardCombatState): void {
    if (!this.juice) return;
    const log = state.log;
    if (this.juiceCursor > log.length) this.juiceCursor = log.length;
    for (let i = this.juiceCursor; i < log.length; i++) {
      const event = log[i];
      if (event) this.juice.playJuice(event, juiceSubject(event, state.activeSide));
    }
    this.juiceCursor = log.length;
  }

  /** Synthesise the display state for the current reveal point: the resolved
   * state with HP rolled back to the revealed events and the log truncated to
   * what's been narrated so far. Block / statuses show their resolved values
   * (the engine exposes no per-event snapshot) — HP and the log carry the beat. */
  private buildPlaybackState(): CardCombatState {
    const pb = this.playback;
    if (!pb) throw new Error('buildPlaybackState called with no playback');
    let playerHp = pb.baselineHp.player;
    let enemyHp = pb.baselineHp.enemy;
    let active: Side = 'enemy';
    for (const event of pb.events.slice(0, pb.revealed)) {
      if (event.kind === 'damage') {
        if (event.target === 'player') playerHp = event.hpAfter;
        else enemyHp = event.hpAfter;
      } else if (event.kind === 'poisonTick') {
        if (event.side === 'player') playerHp = event.hpAfter;
        else enemyHp = event.hpAfter;
      } else if (event.kind === 'turnStart') {
        active = event.side;
      }
    }
    const final = pb.finalState;
    return {
      ...final,
      activeSide: active,
      phase: 'assign',
      victor: null,
      player: { ...final.player, hp: playerHp },
      enemy: { ...final.enemy, hp: enemyHp },
      log: final.log.slice(0, pb.baseLogLength + pb.revealed),
    };
  }

  // ── rendering ───────────────────────────────────────────────────────────

  private renderCombatant(
    state: CardCombatState,
    side: Side,
    combatant: Combatant,
    active: boolean,
    tier?: Rarity,
    enemyFills?: Map<string, EnemyCardFill>,
  ): DocumentFragment {
    const frag = document.createDocumentFragment();
    const card = document.createElement('div');
    card.className = `hex6-card-combat__combatant hex6-card-combat__combatant--${side}`;
    card.dataset.side = side;
    // Active-turn spotlight (ADR-0020 §4a) — the side whose turn it is is lit.
    if (active) card.classList.add('hex6-card-combat__combatant--active');
    // J5 death collapse (ADR-0017 §2). Declarative — applied whenever combat has
    // ended and this side lost, so it survives the post-playback re-render (the
    // keyframe replays on the fresh node); player death then hands to DeathScreen
    // via the existing `reportOutcome('defeat')`.
    if (state.phase === 'ended' && state.victor !== null && state.victor !== side) {
      card.classList.add('hex6-card-combat__combatant--dead');
    }

    // Portrait: medallion (rarity/ember hex frame + glyph) + name/tier, HP,
    // statuses (ADR-0020 §4a). Enemy looms above its medallion; the player reads
    // as a lit left rail beside the 3×3 grid.
    const portrait = document.createElement('div');
    portrait.className = 'hex6-card-combat__portrait';
    portrait.appendChild(this.renderMedallion(side, tier, active));

    const info = document.createElement('div');
    info.className = 'hex6-card-combat__cinfo';
    const head = document.createElement('div');
    head.className = 'hex6-card-combat__chead';
    const name = document.createElement('span');
    name.className = 'hex6-card-combat__name';
    name.textContent = combatant.name;
    head.appendChild(name);
    if (tier) {
      const tierPill = document.createElement('span');
      tierPill.className = `hex6-card-combat__tier hex6-card-combat__tier--${tier}`;
      tierPill.textContent = tier;
      head.appendChild(tierPill);
    }
    info.appendChild(head);
    info.appendChild(this.renderHpLine(side, combatant));
    info.appendChild(this.renderStatuses(combatant.statusBag));
    portrait.appendChild(info);

    // Grid: the player's lit 3×3 loadout / the foe's visible loadout — which is
    // the enemy's intent (ADR-0015 §10; no telegraph).
    const grid = this.renderGrid(state, side, combatant, enemyFills);

    card.append(portrait, grid);
    frag.appendChild(card);
    return frag;
  }

  /** The combatant medallion (ADR-0020 §4a) — a rarity- (enemy) or ember-tinted
   * (player) hex frame holding the foe's identity glyph (HEX-69) or the player's
   * home glyph. Lit on the active turn (the spotlight). */
  private renderMedallion(side: Side, tier: Rarity | undefined, lit: boolean): HTMLDivElement {
    const colour =
      side === 'enemy' ? (tier ? RARITY_COLOUR_VAR[tier] : 'var(--r-common)') : 'var(--ember)';
    const med = document.createElement('div');
    med.className = `hex6-card-combat__medallion hex6-card-combat__medallion--${side}`;
    med.style.setProperty('--med-colour', colour);
    if (lit) med.classList.add('hex6-card-combat__medallion--lit');

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 110 110');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'hex6-card-combat__medallion-frame');
    const outer = document.createElementNS(SVG_NS, 'polygon');
    outer.setAttribute('points', '55,4 99,29 99,81 55,106 11,81 11,29');
    outer.setAttribute('class', 'hex6-card-combat__medallion-hex');
    const inner = document.createElementNS(SVG_NS, 'polygon');
    inner.setAttribute('points', '55,14 90,34 90,76 55,96 20,76 20,34');
    inner.setAttribute(
      'class',
      'hex6-card-combat__medallion-hex hex6-card-combat__medallion-hex--inner',
    );
    svg.append(outer, inner);
    med.appendChild(svg);

    const glyphHost = document.createElement('div');
    glyphHost.className = 'hex6-card-combat__medallion-glyph';
    if (side === 'enemy' && this.enemyGlyph) {
      // The foe's identity glyph (HEX-69) — kept as the named hook the tests and
      // juice rely on, now seated inside the medallion.
      const glyph = document.createElement('span');
      glyph.className = 'hex6-card-combat__enemy-glyph';
      glyph.dataset.role = 'enemy-glyph';
      glyph.textContent = this.enemyGlyph;
      glyphHost.appendChild(glyph);
    } else {
      glyphHost.appendChild(
        createGlyphElement(side === 'enemy' ? 'skull' : 'home', {
          size: side === 'enemy' ? 46 : 34,
          colour,
        }),
      );
    }
    med.appendChild(glyphHost);
    return med;
  }

  /** Centre dice tray (ADR-0020 §4a) — an etched panel holding the active side's
   * rolled pool, with the assign-affordance hint above. Both side pools render
   * (the named `data-side` hooks the §11.2 visibility tests probe); only the
   * active side's is shown. */
  private renderTray(state: CardCombatState, paced: boolean, tumble: boolean): DocumentFragment {
    const frag = document.createDocumentFragment();
    const panel = document.createElement('div');
    panel.className = 'hex6-card-combat__tray-panel etch';
    for (const c of ['tl', 'tr', 'bl', 'br']) {
      const corner = document.createElement('span');
      corner.className = `corner ${c}`;
      panel.appendChild(corner);
    }

    const hint = document.createElement('div');
    hint.className = 'hex6-card-combat__tray-hint';
    hint.textContent =
      state.phase === 'ended'
        ? '—'
        : paced
          ? 'Enemy acting…'
          : state.activeSide === 'player'
            ? 'Your turn · drag a die onto a card'
            : 'Enemy turn';
    panel.appendChild(hint);

    const pools = document.createElement('div');
    pools.className = 'hex6-card-combat__tray-pools';
    for (const side of ['enemy', 'player'] as Side[]) {
      // During the paced enemy turn the tray holds the enemy's rolled pool (the
      // dice that fly into its card slots, ADR-0021 §2); otherwise the active
      // side's pool shows on its own turn (§11.2).
      if (paced && this.enemyView && side === 'enemy') {
        pools.appendChild(this.renderEnemyPool(this.enemyView.trayDice, this.enemyView.tumble));
      } else {
        const show = !paced && state.phase !== 'ended' && state.activeSide === side;
        pools.appendChild(this.renderDice(state, side, show, tumble && show));
      }
    }
    panel.appendChild(pools);

    frag.appendChild(panel);
    return frag;
  }

  private renderHpLine(side: Side, combatant: Combatant): HTMLDivElement {
    const line = document.createElement('div');
    line.className = 'hex6-card-combat__hpline';

    const bar = document.createElement('div');
    bar.className = 'hex6-card-combat__hpbar';
    const fill = document.createElement('div');
    fill.className = `hex6-card-combat__hpfill hex6-card-combat__hpfill--${side}`;
    fill.dataset.side = side;
    const ratio = combatant.maxHp === 0 ? 0 : clamp01(combatant.hp / combatant.maxHp);
    fill.style.width = `${ratio * 100}%`;
    bar.appendChild(fill);

    const tail = document.createElement('div');
    tail.className = 'hex6-card-combat__hptail';

    const text = document.createElement('span');
    text.className = 'hex6-card-combat__hptext';
    const value = document.createElement('b');
    value.textContent = `${combatant.hp}/${combatant.maxHp}`;
    text.append('HP ', value);

    const block = document.createElement('span');
    block.className = 'hex6-card-combat__block';
    if (combatant.block <= 0) block.hidden = true;
    else block.textContent = `◈ ${combatant.block} block`;

    tail.append(text, block);
    line.append(bar, tail);
    return line;
  }

  private renderStatuses(bag: readonly StatusApplication[]): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'hex6-card-combat__statuses';
    if (bag.length === 0) {
      const empty = document.createElement('span');
      empty.className = 'hex6-card-combat__status hex6-card-combat__status--empty';
      empty.textContent = '[no effects]';
      row.appendChild(empty);
      return row;
    }
    for (const entry of bag) {
      const pill = document.createElement('span');
      pill.className = 'hex6-card-combat__status';
      pill.dataset.status = entry.status;
      const icon = STATUS_ICON[entry.status] ?? '?';
      pill.textContent = `${icon} ${entry.magnitude} ×${entry.duration}`;
      pill.title = `${entry.status} ${entry.magnitude}, ${entry.duration} turn(s) remaining`;
      row.appendChild(pill);
    }
    return row;
  }

  private renderDice(
    state: CardCombatState,
    side: Side,
    show: boolean,
    tumble: boolean,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'hex6-card-combat__dice';
    row.dataset.side = side;
    if (!show) {
      row.classList.add('hex6-card-combat__dice--hidden');
      return row;
    }
    const interactive =
      side === 'player' && state.activeSide === 'player' && state.phase === 'assign';
    for (const slot of state.pool) {
      row.appendChild(this.renderDie(slot, interactive, tumble));
    }
    // The pool is a drop target: dragging an assigned die's fed chip back here
    // unassigns just that die (HEX-59 drag-back).
    if (interactive) {
      row.addEventListener('dragover', (e) => this.handlePoolDragOver(e, state));
      row.addEventListener('dragleave', () =>
        row.classList.remove('hex6-card-combat__dice--drop-ready'),
      );
      row.addEventListener('drop', (e) => this.handlePoolDrop(e, row));
    }
    return row;
  }

  private renderDie(slot: DieSlot, interactive: boolean, tumble: boolean): HTMLElement {
    // Perspective container — all behaviour (drag, click, select/assign/reject,
    // data-die-index, title) attaches here, unchanged. The 3D cube inside it is
    // purely visual (ADR-0017 §1 / Consequences).
    const node = document.createElement('div');
    node.className = 'hex6-card-combat__die';
    node.setAttribute(DATA_DIE_INDEX, String(slot.index));
    node.dataset.face = String(slot.face);
    node.title = `Die ${slot.index} — face ${slot.face} (${DIE_GLYPHS[slot.face] ?? slot.face})`;
    node.appendChild(this.buildCube(slot, tumble));
    if (slot.assignedTo !== null) {
      node.classList.add('hex6-card-combat__die--assigned');
      node.dataset.assignedTo = slot.assignedTo;
    }
    if (this.selectedDieIndex === slot.index) {
      node.classList.add('hex6-card-combat__die--selected');
    }
    if (interactive) {
      node.draggable = slot.assignedTo === null;
      if (slot.assignedTo === null) {
        node.addEventListener('dragstart', (e) => this.handleDieDragStart(e, slot.index));
      }
      node.addEventListener('click', (e) => this.handleDieClick(e, slot));
    }
    return node;
  }

  /** The 3D cube subtree: a `preserve-3d` cube of six pip faces (opposite faces
   * sum to 7). `tumble` seeds the cube at its spun-up start pose so `kickTumbles`
   * can transition it to rest after mount; otherwise it sits at rest (ADR-0017
   * §1). The cube carries no behaviour — it's a visual child of the die node. */
  private buildCube(slot: DieSlot, tumble: boolean): HTMLDivElement {
    const cube = document.createElement('div');
    cube.className = 'hex6-card-combat__cube';
    const rest = faceRestPose(slot.face);
    cube.dataset.restPose = rest;
    cube.style.setProperty('--die-index', String(slot.index));
    if (tumble) {
      cube.dataset.tumble = 'pending';
      cube.style.transform = tumbleStartPose(slot.face, slot.index);
    } else {
      cube.style.transform = rest;
    }
    for (const face of [1, 2, 3, 4, 5, 6] as const) {
      const faceEl = document.createElement('div');
      faceEl.className = `hex6-card-combat__face hex6-card-combat__face--${face}`;
      const lit = facePips(face);
      for (let cell = 1; cell <= 9; cell++) {
        const span = document.createElement('span');
        if (lit.includes(cell)) span.className = 'hex6-card-combat__pip';
        faceEl.appendChild(span);
      }
      cube.appendChild(faceEl);
    }
    return cube;
  }

  /** Kick every freshly-mounted cube into its tumble: commit the start pose with
   * a reflow, then transition to the rest pose (the long way round). `will-change`
   * is set for the duration and cleared on `transitionend`, with a timeout
   * backstop so a dropped event can't leave it stuck on (ADR-0017 §4). */
  private kickTumbles(): void {
    if (!this.root) return;
    const cubes = this.root.querySelectorAll<HTMLElement>(
      '.hex6-card-combat__cube[data-tumble="pending"]',
    );
    cubes.forEach((cube, i) => {
      const rest = cube.dataset.restPose;
      if (rest === undefined) return;
      cube.style.willChange = 'transform';
      // Force a reflow so the spun-up start pose is the transition's baseline.
      void cube.offsetWidth;
      cube.classList.add(TUMBLE_CLASS);
      cube.dataset.tumble = 'running';
      cube.style.transform = rest;
      const settle = (): void => {
        cube.classList.remove(TUMBLE_CLASS);
        cube.style.willChange = '';
        cube.removeAttribute('data-tumble');
        cube.removeEventListener('transitionend', settle);
        const t = this.tumbleCleanupTimers.get(cube);
        if (t !== undefined) {
          clearTimeout(t);
          this.tumbleCleanupTimers.delete(cube);
        }
      };
      cube.addEventListener('transitionend', settle);
      const backstop = setTimeout(
        settle,
        TUMBLE_MS + i * TUMBLE_STAGGER_MS + 80,
      ) as unknown as number;
      this.tumbleCleanupTimers.set(cube, backstop);
    });
  }

  private renderGrid(
    state: CardCombatState,
    side: Side,
    combatant: Combatant,
    enemyFills?: Map<string, EnemyCardFill>,
  ): HTMLDivElement {
    const grid = document.createElement('div');
    grid.className = 'hex6-card-combat__grid';
    grid.dataset.side = side;

    const { placements } = combatant.inventory.loadout;
    // ADR-0021 §1 — the footprint packing is dropped in combat: cards lay out as a
    // uniform hand / wrapped row at one "playing card" size, not their packed
    // grid positions. The CSS turns `.grid` into a flex-wrap row. The player's
    // hand is ordered by the engine's row-major resolution walk (cardEngine §11.3)
    // so the 1→N numerals read left-to-right; the foe's loadout (its visible
    // intent, ADR-0015 §10) keeps placement order and carries no numerals.
    const ordered =
      side === 'player'
        ? [...placements].sort((a, b) => a.origin.row - b.origin.row || a.origin.col - b.origin.col)
        : placements;

    const playerTurn =
      side === 'player' && state.activeSide === 'player' && state.phase === 'assign';
    ordered.forEach((placement, i) => {
      const orderNo = side === 'player' ? i + 1 : null;
      grid.appendChild(
        this.renderCard(
          state,
          placement,
          playerTurn,
          combatant,
          orderNo,
          enemyFills?.get(placement.card.id),
        ),
      );
    });
    return grid;
  }

  private renderCard(
    state: CardCombatState,
    placement: GridPlacement,
    interactive: boolean,
    owner: Combatant,
    orderNo: number | null = null,
    enemyFill?: EnemyCardFill,
  ): HTMLElement {
    const card = placement.card;
    const assignment = state.assignments.find((a) => a.cardId === card.id);
    const assignedCount = assignment?.dieIndices.length ?? 0;

    // HEX-133 — the picked-up die (click-select), on the player's turn only. Drives
    // the eligibility glow below: the cards this die can feed and the open slots its
    // face satisfies light up, so the click-then-click flow shows where the die lands
    // (and which cards its face bonuses, e.g. +6 block on a 1). Drag has its own
    // per-card / per-slot green-red affordance; this covers selection.
    // A packed die renders as the pip die-chip in combat too (ADR-0024 §5): it is
    // part of the roll source, not an assignable card, so it carries no slots,
    // order numeral, or §19 adornments — return the chip before the card-face path.
    if (isDieCard(card)) {
      const chip = renderGridDieChip(card.dieType ?? PLAIN_D6);
      chip.classList.add('hex6-card-combat__card', 'hex6-card-combat__card--die');
      chip.setAttribute(DATA_CARD_ID, card.id);
      return chip;
    }

    const selectedDie =
      interactive && this.selectedDieIndex !== null ? state.pool[this.selectedDieIndex] : undefined;
    const selFace = selectedDie && selectedDie.assignedTo === null ? selectedDie.face : undefined;

    // Unified card face (HEX-103, COMBAT mode, ADR-0021 §1) — every card renders
    // at one uniform "playing card" size with dashed die-slots (the footprint
    // packing is dropped in combat). Combat layers its live §19 / HEX-88
    // adornments on top; the engine + interaction logic below is untouched
    // (presentation-only). `rotated` is irrelevant to the uniform face but kept
    // for the hover panel below.
    const el = renderCardFace(card, { mode: 'combat', rotated: placement.rotated });
    el.classList.add(
      'hex6-card-combat__card',
      `hex6-card-combat__card--${card.type}`,
      `hex6-card-combat__card--${card.rarity}`,
    );
    el.setAttribute(DATA_CARD_ID, card.id);

    // Resolution-order index (player grid only, ADR-0020 §4a) — a corner numeral.
    if (orderNo !== null) {
      const order = document.createElement('div');
      order.className = 'hex6-card-combat__card-order';
      order.textContent = String(orderNo);
      el.appendChild(order);
    }

    // In-flow adornments sit just above the footprint foot.
    const body = el.querySelector<HTMLElement>('.hex6-card__body') ?? el;
    const foot = body.querySelector<HTMLElement>('.hex6-card__foot');
    const insert = (node: HTMLElement): void => {
      body.insertBefore(node, foot);
    };

    // §19 cost-face bonus badge (static, e.g. "+6 block on 1").
    const bonus = costBonusBadge(card);
    if (bonus) {
      const b = document.createElement('div');
      b.className = 'hex6-card-combat__card-bonus';
      b.textContent = bonus;
      insert(b);
    }

    // §19 extra-die scaling rule (static, e.g. "+2 dmg/extra die") — generalised
    // off `extraCost` so the per-extra rule reads on the face, not only the live
    // preview number below (HEX-97).
    const scaling = extraCostBadge(card);
    if (scaling) {
      const s = document.createElement('div');
      s.className = 'hex6-card-combat__card-scaling';
      s.textContent = scaling;
      insert(s);
    }

    // §19 scaling preview — projected primary value as extra dice stack
    // (5→7→9→11 on Power Strike). Baseline shown even with no dice yet.
    const preview = scaledPreview(card, assignedCount - card.cost.length);
    if (preview) {
      const p = document.createElement('div');
      p.className = 'hex6-card-combat__card-preview';
      p.textContent = `→ ${preview.value} ${preview.kind === 'damage' ? 'dmg' : 'block'}`;
      insert(p);
    }

    // §19 once-per-turn "spent" marker (e.g. Reroll used this turn).
    if (card.oncePerTurn && owner.oncePerTurnFired.has(card.id)) {
      el.classList.add('hex6-card-combat__card--spent');
      const spent = document.createElement('div');
      spent.className = 'hex6-card-combat__card-spent';
      spent.textContent = 'spent';
      insert(spent);
    }

    // HEX-88 — frozen indicator (Cold Snap, ADR-0015 §11.4). A frozen card is
    // un-fed for `turns` more turns; surface that as an ice tint + remaining-
    // turns badge so the lock-out is legible instead of a silent no-assign. The
    // frost is an absolute overlay → mounted on the card root, over the face.
    const frozenTurns = owner.frozenCards.get(card.id) ?? 0;
    if (frozenTurns > 0) {
      el.classList.add('hex6-card-combat__card--frozen');
      const frost = document.createElement('div');
      frost.className = 'hex6-card-combat__card-frost';
      frost.dataset.role = 'card-frost';
      const badge = document.createElement('span');
      badge.className = 'hex6-card-combat__card-frost-badge';
      badge.textContent = `❄ ${frozenTurns}`;
      badge.title = `Frozen — thaws in ${frozenTurns} ${frozenTurns === 1 ? 'turn' : 'turns'}`;
      frost.appendChild(badge);
      el.appendChild(frost);
    }

    // HEX-133 — eligibility glow: if the picked-up die can legally feed this card,
    // ring it and tag *why* (`dieCardLink` → cost / extra / bonus) so the cue reads
    // "this die goes here, and it does X". `canAccept` is the engine-faithful gate
    // (excludes full / mismatched / frozen / non-assignable cards), so a glowing
    // card is always a valid drop.
    if (selectedDie !== undefined && this.canAccept(card, selectedDie, state)) {
      el.classList.add('hex6-card-combat__card--die-eligible');
      const link = dieCardLink(selectedDie.face, card);
      if (link) el.dataset.dieLink = link;
    }

    this.hoverPanel.bind(el, () => ({ card, rotated: placement.rotated }));

    // Fill the die-slots (ADR-0021 §1). Each cost predicate has a dashed slot
    // (built by the combat-mode face); a committed die lands in the slot whose
    // predicate it satisfies, showing its face. Each slot is an addressable drop
    // target (HEX-114 will animate enemy dice into specific slots), and on the
    // player's turn its filled chip is a per-die unassign handle (click / drag
    // back to the pool, HEX-59). Dice assigned past the cost slots (§19 extraCost
    // scaling) overflow into a trailing chip row.
    if (enemyFill) {
      // ADR-0021 §2 — enemy-turn playback: the slots are filled from the
      // reconstructed feed (not `state.assignments`, which the engine has cleared),
      // showing the rolled die face. Static + non-interactive; the dice fly in via
      // `flyEnemyDiceForCard` and the chip is its landing target.
      const slotEls = el.querySelectorAll<HTMLElement>('.hex6-card__slot');
      slotEls.forEach((slotEl, i) => {
        const f = enemyFill.slots.get(i);
        if (f === undefined) return;
        slotEl.classList.add('hex6-card__slot--filled');
        slotEl.dataset.dieIndex = String(f.dieIndex);
        slotEl.replaceChildren(buildEnemyFedChip(f.face, f.dieIndex));
      });
      if (enemyFill.extras.length > 0) {
        const extra = document.createElement('div');
        extra.className = 'hex6-card-combat__card-extra';
        for (const e of enemyFill.extras) extra.appendChild(buildEnemyFedChip(e.face, e.dieIndex));
        insert(extra);
      }
    } else {
      const dice = (assignment?.dieIndices ?? []).flatMap((idx) => {
        const slot = state.pool[idx];
        return slot ? [{ index: idx, face: slot.face }] : [];
      });
      const { slotFill, extras } = matchDiceToSlots(card.cost, dice);
      const slotEls = el.querySelectorAll<HTMLElement>('.hex6-card__slot');
      const slotInteractive = interactive && isAssignable(card);
      slotEls.forEach((slotEl, i) => {
        const dieIndex = slotFill[i];
        if (dieIndex !== null && dieIndex !== undefined) {
          slotEl.classList.add('hex6-card__slot--filled');
          slotEl.dataset.dieIndex = String(dieIndex);
          slotEl.replaceChildren(this.buildFedChip(state, dieIndex, interactive));
        } else if (selFace !== undefined) {
          // HEX-133 — an open slot whose predicate the picked-up die satisfies.
          const predicate = card.cost[i];
          if (predicate !== undefined && matches(selFace, predicate)) {
            slotEl.classList.add('hex6-card__slot--eligible');
          }
        }
        if (slotInteractive) this.wireSlotDrop(slotEl, card, state, i);
      });
      if (extras.length > 0) {
        const extra = document.createElement('div');
        extra.className = 'hex6-card-combat__card-extra';
        for (const dieIndex of extras)
          extra.appendChild(this.buildFedChip(state, dieIndex, interactive));
        insert(extra);
      }
    }

    if (interactive && isAssignable(card)) {
      el.classList.add('hex6-card-combat__card--assignable');
      el.addEventListener('click', () => this.handleCardClick(card));
      el.addEventListener('dragover', (e) => this.handleCardDragOver(e, card, state));
      el.addEventListener('dragleave', () => {
        el.classList.remove('hex6-card-combat__card--drop-ready');
        el.classList.remove('hex6-card-combat__card--drop-reject');
      });
      el.addEventListener('drop', (e) => this.handleCardDrop(e, card));
    } else if (interactive && isActivatable(card) && !owner.oncePerTurnFired.has(card.id)) {
      // §19 free activated card (Reroll): click to fire the chosen-die reroll.
      el.classList.add('hex6-card-combat__card--activatable');
      el.addEventListener('click', () => this.handleActivate(card));
    }
    return el;
  }

  /** A committed-die chip (the die's face glyph) seated in a filled slot or the
   * extra-dice overflow. On the player's turn it is a per-die unassign handle:
   * click it or drag it back to the pool to release just that die (HEX-59). */
  private buildFedChip(
    state: CardCombatState,
    dieIndex: number,
    interactive: boolean,
  ): HTMLSpanElement {
    const slot = state.pool[dieIndex];
    const chip = document.createElement('span');
    chip.className = 'hex6-card-combat__fed-die';
    chip.setAttribute(DATA_DIE_INDEX, String(dieIndex));
    chip.textContent = slot ? (DIE_GLYPHS[slot.face] ?? String(slot.face)) : '·';
    if (interactive) {
      chip.classList.add('hex6-card-combat__fed-die--interactive');
      chip.title = 'Click or drag out to unassign';
      chip.draggable = true;
      chip.addEventListener('dragstart', (e) => this.handleDieDragStart(e, dieIndex));
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        this.emit({ kind: 'unassignDie', dieIndex });
      });
    }
    return chip;
  }

  /** Make a single die-slot a drop target (ADR-0021 §1). Dropping a die assigns it
   * to the card (the engine still picks which cost slot it pays — this only
   * targets the visual landing place). The slot lights green/red by whether the
   * dragged die fits *this* slot's predicate and the card can take it; the drop
   * itself goes through `assign`, which is the authority and shakes on reject. */
  private wireSlotDrop(
    slotEl: HTMLElement,
    card: Card,
    state: CardCombatState,
    slotIndex: number,
  ): void {
    const predicate = card.cost[slotIndex];
    slotEl.addEventListener('dragover', (e) => {
      const dieIndex = readDragDieIndex(e);
      if (dieIndex === null) return;
      const slot = state.pool[dieIndex];
      if (slot === undefined) return;
      e.preventDefault();
      e.stopPropagation();
      const open = !slotEl.classList.contains('hex6-card__slot--filled');
      const ok =
        open &&
        predicate !== undefined &&
        matches(slot.face, predicate) &&
        this.canAccept(card, slot, state);
      slotEl.classList.toggle('hex6-card__slot--drop-ready', ok);
      slotEl.classList.toggle('hex6-card__slot--drop-reject', !ok);
      if (e.dataTransfer) e.dataTransfer.dropEffect = ok ? 'move' : 'none';
    });
    slotEl.addEventListener('dragleave', () => {
      slotEl.classList.remove('hex6-card__slot--drop-ready', 'hex6-card__slot--drop-reject');
    });
    slotEl.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      slotEl.classList.remove('hex6-card__slot--drop-ready', 'hex6-card__slot--drop-reject');
      const dieIndex = readDragDieIndex(e);
      if (dieIndex === null) return;
      this.assign(card, dieIndex);
    });
  }

  private renderLog(state: CardCombatState): void {
    if (!this.logMount) return;
    const namer: SideNamer = (side) => (side === 'player' ? 'You' : state.enemy.name);
    // Full history, latest at the bottom — the band is scrollable so nothing is
    // dropped (HEX-69). During enemy narration `state.log` is the revealed slice,
    // so the same render shows exactly what's been narrated so far.
    const lines = state.log.map((event) => {
      const line = document.createElement('div');
      line.className = 'hex6-card-combat__log-line';
      // HEX-88 — render fresh-roll faces as actual dice (reusing DIE_GLYPHS) so a
      // recursive roll READS as dice landing, not just numbers in prose. Other
      // events stay plain text.
      if (event.kind === 'rollFresh') {
        renderRollFreshLine(line, event, namer);
      } else {
        line.textContent = `› ${formatEvent(event, namer)}`;
      }
      return line;
    });
    this.logMount.replaceChildren(...lines);
    this.logMount.scrollTop = this.logMount.scrollHeight;
  }

  // ── enemy-turn dice animation (ADR-0021 §2) ────────────────────────────────

  /** The enemy's rolled pool during paced playback — the dice not yet flown into a
   * card slot, as non-interactive 3D cubes. They tumble in on the opening render
   * (the "Enemy rolls X Y" beat) and thin out as the bot spends them into slots. */
  private renderEnemyPool(
    trayDice: ReadonlyArray<{ index: number; face: DieFace }>,
    tumble: boolean,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'hex6-card-combat__dice';
    row.dataset.side = 'enemy';
    for (const d of trayDice) {
      row.appendChild(
        this.renderDie({ index: d.index, face: d.face, assignedTo: null }, false, tumble),
      );
    }
    return row;
  }

  /** Snapshot each enemy tray die's on-screen rect by pool index, before the
   * per-step re-render swaps the tray out — a flying die launches from where it
   * currently sits (ADR-0021 §2). */
  private captureEnemyTrayRects(): Map<number, DOMRect> {
    const rects = new Map<number, DOMRect>();
    const dice = this.root?.querySelectorAll<HTMLElement>(
      `.hex6-card-combat__dice[data-side="enemy"] .hex6-card-combat__die[${DATA_DIE_INDEX}]`,
    );
    for (const die of dice ?? []) {
      const idx = Number(die.getAttribute(DATA_DIE_INDEX));
      if (Number.isInteger(idx)) rects.set(idx, die.getBoundingClientRect());
    }
    return rects;
  }

  /** Fly the dice feeding `cardId` from their tray positions (`sources`) into the
   * card's now-rendered slots / extra chips (ADR-0021 §2). The landing chip is
   * hidden until its cube arrives, so the die READS as delivered by the throw. */
  private flyEnemyDiceForCard(cardId: string, sources: Map<number, DOMRect>): void {
    if (!this.root || !this.flightLayer || prefersReducedMotion()) return;
    const fill = this.enemyView?.fills.get(cardId);
    const cardEl = this.root.querySelector<HTMLElement>(
      `.hex6-card-combat__combatant--enemy [${DATA_CARD_ID}="${cardId}"]`,
    );
    if (!fill || !cardEl) return;
    const rootRect = this.root.getBoundingClientRect();
    const flyTo = (dieIndex: number, face: DieFace, chip: HTMLElement | null): void => {
      const from = sources.get(dieIndex);
      if (!from || !chip) return;
      chip.style.visibility = 'hidden';
      const to = chip.getBoundingClientRect();
      this.spawnFlight(face, from, to, rootRect, () => {
        chip.style.visibility = '';
        chip.classList.add('hex6-card-combat__fed-die--land');
      });
    };
    for (const [slotIndex, f] of fill.slots) {
      flyTo(
        f.dieIndex,
        f.face,
        cardEl.querySelector<HTMLElement>(
          `.hex6-card__slot[data-slot-index="${slotIndex}"] .hex6-card-combat__fed-die`,
        ),
      );
    }
    for (const e of fill.extras) {
      flyTo(
        e.dieIndex,
        e.face,
        cardEl.querySelector<HTMLElement>(
          `.hex6-card-combat__card-extra .hex6-card-combat__fed-die[${DATA_DIE_INDEX}="${e.dieIndex}"]`,
        ),
      );
    }
  }

  /** Spawn one flying cube and transition it from `from` to `to` (rects in viewport
   * space; positioned relative to `rootRect`). On arrival — `transitionend` or a
   * backstop timer — it removes itself and runs `onLand` (ADR-0021 §2). */
  private spawnFlight(
    face: DieFace,
    from: DOMRect,
    to: DOMRect,
    rootRect: DOMRect,
    onLand: () => void,
  ): void {
    const layer = this.flightLayer;
    if (!layer) {
      onLand();
      return;
    }
    const node = document.createElement('div');
    node.className = FLY_CLASS;
    node.style.left = `${from.left - rootRect.left}px`;
    node.style.top = `${from.top - rootRect.top}px`;
    node.appendChild(this.buildCube({ index: 0, face, assignedTo: null }, false));
    layer.appendChild(node);
    // Translate by the centre-to-centre delta; a little spin sells the throw.
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    void node.offsetWidth; // commit the start pose as the transition baseline
    node.classList.add('hex6-card-combat__flight-die--go');
    node.style.transform = `translate(${dx}px, ${dy}px)`;
    let done = false;
    const settle = (): void => {
      if (done) return;
      done = true;
      node.removeEventListener('transitionend', settle);
      const t = this.flightNodeTimers.get(node);
      if (t !== undefined) {
        clearTimeout(t);
        this.flightNodeTimers.delete(node);
      }
      node.remove();
      onLand();
    };
    node.addEventListener('transitionend', settle);
    const backstop = setTimeout(settle, FLY_MS + 80) as unknown as number;
    this.flightNodeTimers.set(node, backstop);
  }

  /** Tear down any in-flight cubes + their backstop timers (skip / destroy). */
  private clearFlights(): void {
    for (const t of this.flightNodeTimers.values()) clearTimeout(t);
    this.flightNodeTimers.clear();
    if (this.flightLayer) this.flightLayer.replaceChildren();
  }

  // ── input handlers ────────────────────────────────────────────────────

  private handleDieDragStart(e: DragEvent, dieIndex: number): void {
    if (!e.dataTransfer) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DRAG_MIME, String(dieIndex));
    e.dataTransfer.setData('text/plain', String(dieIndex));
  }

  private handleCardDragOver(e: DragEvent, card: Card, state: CardCombatState): void {
    const dieIndex = readDragDieIndex(e);
    if (dieIndex === null) return;
    const slot = state.pool[dieIndex];
    if (slot === undefined) return;
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    if (this.canAccept(card, slot, state)) {
      el.classList.add('hex6-card-combat__card--drop-ready');
      el.classList.remove('hex6-card-combat__card--drop-reject');
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    } else {
      el.classList.add('hex6-card-combat__card--drop-reject');
      el.classList.remove('hex6-card-combat__card--drop-ready');
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    }
  }

  private handleCardDrop(e: DragEvent, card: Card): void {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    el.classList.remove('hex6-card-combat__card--drop-ready');
    el.classList.remove('hex6-card-combat__card--drop-reject');
    const dieIndex = readDragDieIndex(e);
    if (dieIndex === null) return;
    this.assign(card, dieIndex);
  }

  private handleDieClick(e: MouseEvent, slot: DieSlot): void {
    e.stopPropagation();
    const state = this.state;
    if (!state || state.phase === 'ended' || state.activeSide !== 'player') return;
    if (slot.assignedTo !== null) {
      // Click an assigned die → release just that die (HEX-59). Whole-card clear
      // stays on the card body (handleCardClick with no die selected).
      this.emit({ kind: 'unassignDie', dieIndex: slot.index });
      return;
    }
    this.selectedDieIndex = this.selectedDieIndex === slot.index ? null : slot.index;
    this.update(state);
  }

  /** Drag-back drop target (HEX-59): an assigned die dragged onto the pool is
   * unassigned. Unassigned dice dragged here are inert (they already live here). */
  private handlePoolDragOver(e: DragEvent, state: CardCombatState): void {
    const dieIndex = readDragDieIndex(e);
    if (dieIndex === null) return;
    const slot = state.pool[dieIndex];
    if (slot === undefined || slot.assignedTo === null) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    (e.currentTarget as HTMLElement).classList.add('hex6-card-combat__dice--drop-ready');
  }

  private handlePoolDrop(e: DragEvent, row: HTMLElement): void {
    e.preventDefault();
    row.classList.remove('hex6-card-combat__dice--drop-ready');
    const dieIndex = readDragDieIndex(e);
    if (dieIndex === null) return;
    const slot = this.state?.pool[dieIndex];
    if (!slot || slot.assignedTo === null) return;
    this.emit({ kind: 'unassignDie', dieIndex });
  }

  private handleCardClick(card: Card): void {
    const state = this.state;
    if (!state || state.phase !== 'assign' || state.activeSide !== 'player') return;
    if (this.selectedDieIndex !== null) {
      this.assign(card, this.selectedDieIndex);
      return;
    }
    // No die selected — clicking a fed card clears it.
    const assignment = state.assignments.find((a) => a.cardId === card.id);
    if (assignment && assignment.dieIndices.length > 0) {
      this.emit({ kind: 'clearCard', cardId: card.id });
    }
  }

  private handleEndTurn(): void {
    const state = this.state;
    if (!state || state.phase !== 'assign' || state.activeSide !== 'player') return;
    this.selectedDieIndex = null;
    this.emit({ kind: 'endTurn' });
  }

  private onKey(e: KeyboardEvent): void {
    const state = this.state;
    if (!state) return;
    // While the enemy turn narrates, Enter / Space / Escape fast-forward it.
    if (this.playback) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        this.skipEnemyPlayback();
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Escape') {
      if (this.retreatPrompt) {
        this.dismissRetreatPrompt();
        e.preventDefault();
        return;
      }
      if (this.selectedDieIndex !== null) {
        this.selectedDieIndex = null;
        this.update(state);
        e.preventDefault();
        return;
      }
      // §19: Esc no longer forfeits outright — it opens the retreat-roll prompt
      // (player turn only; the roll itself is the cost). On the enemy turn there
      // is nothing to retreat from yet, so ignore.
      if (state.phase !== 'ended' && state.activeSide === 'player') {
        this.openRetreatPrompt();
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Enter' && state.phase === 'assign' && state.activeSide === 'player') {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'BUTTON' || target.tagName === 'INPUT')) return;
      this.handleEndTurn();
    }
  }

  private assign(card: Card, dieIndex: number): void {
    const state = this.state;
    if (!state) return;
    // HEX-88 — a frozen card silently swallowed dice (engine throws, UI just
    // shook the die). Reject up front and name the reason so the lock-out reads
    // as intentional, not as a broken drop. `canAccept` doesn't model freeze
    // (the engine is the authority), so this guard owns it on the UI side.
    const frozenTurns = state.player.frozenCards.get(card.id) ?? 0;
    if (frozenTurns > 0) {
      this.rejectDie(dieIndex);
      this.showRejectReason(
        `${card.name} is frozen — thaws in ${frozenTurns} ${frozenTurns === 1 ? 'turn' : 'turns'}`,
      );
      return;
    }
    const slot = state.pool[dieIndex];
    if (slot === undefined || !this.canAccept(card, slot, state)) {
      this.rejectDie(dieIndex);
      return;
    }
    this.selectedDieIndex = null;
    this.emit({ kind: 'assignDie', dieIndex, cardId: card.id });
  }

  /** Activate a free, once-per-turn card (ADR-0015 §19; Reroll). The selected die
   * is the reroll target ("player picks the die"); with none selected the engine
   * rerolls the lowest free die. */
  private handleActivate(card: Card): void {
    const state = this.state;
    if (!state || state.phase !== 'assign' || state.activeSide !== 'player') return;
    if (state.player.oncePerTurnFired.has(card.id)) return;
    const dieIndex = this.selectedDieIndex ?? this.firstFreeDie(state);
    this.selectedDieIndex = null;
    this.emit(
      dieIndex === null
        ? { kind: 'activateCard', cardId: card.id }
        : { kind: 'activateCard', cardId: card.id, dieIndex },
    );
  }

  private firstFreeDie(state: CardCombatState): number | null {
    const slot = state.pool.find((s) => s.assignedTo === null);
    return slot ? slot.index : null;
  }

  // ── retreat (§19) ─────────────────────────────────────────────────────────

  /** Open the retreat-roll confirm prompt (ADR-0015 §19). */
  private openRetreatPrompt(): void {
    if (this.retreatPrompt || !this.root) return;
    const prompt = document.createElement('div');
    prompt.className = 'hex6-card-combat__retreat';
    prompt.dataset.role = 'retreat-prompt';

    const msg = document.createElement('div');
    msg.className = 'hex6-card-combat__retreat-msg';
    msg.textContent =
      'Retreat? Roll a die — odd: you slip away · even: you forfeit your next turn.';

    const actions = document.createElement('div');
    actions.className = 'hex6-card-combat__retreat-actions';
    const go = document.createElement('button');
    go.type = 'button';
    go.dataset.action = 'retreat-roll';
    go.textContent = 'Roll to retreat';
    go.addEventListener('click', () => {
      this.dismissRetreatPrompt();
      this.emit({ kind: 'attemptRetreat' });
    });
    const stay = document.createElement('button');
    stay.type = 'button';
    stay.dataset.action = 'retreat-cancel';
    stay.textContent = 'Stay';
    stay.addEventListener('click', () => this.dismissRetreatPrompt());
    actions.append(go, stay);

    prompt.append(msg, actions);
    this.root.appendChild(prompt);
    this.retreatPrompt = prompt;
  }

  private dismissRetreatPrompt(): void {
    this.retreatPrompt?.remove();
    this.retreatPrompt = null;
  }

  /** Transient roll-outcome message (ADR-0015 §19). */
  private showRetreatToast(text: string): void {
    if (!this.root) return;
    this.retreatToast?.remove();
    const toast = document.createElement('div');
    toast.className = 'hex6-card-combat__retreat-toast';
    toast.dataset.role = 'retreat-toast';
    toast.textContent = text;
    this.root.appendChild(toast);
    this.retreatToast = toast;
  }

  /** HEX-88 — transient reason for a rejected die drop (frozen card). Auto-clears;
   * positioned near the dice tray so it sits beside the failed drop, not over the
   * board. Reuses the toast chrome. */
  private showRejectReason(text: string): void {
    if (!this.root) return;
    this.rejectReasonToast?.remove();
    if (this.rejectReasonTimer !== null) clearTimeout(this.rejectReasonTimer);
    const toast = document.createElement('div');
    toast.className = 'hex6-card-combat__reject-reason';
    toast.dataset.role = 'reject-reason';
    toast.textContent = text;
    this.root.appendChild(toast);
    this.rejectReasonToast = toast;
    this.rejectReasonTimer = setTimeout(() => {
      this.rejectReasonToast?.remove();
      this.rejectReasonToast = null;
      this.rejectReasonTimer = null;
    }, REJECT_REASON_MS) as unknown as number;
  }

  private emit(intent: CardEngineIntent): void {
    this.onIntent?.(intent);
  }

  /**
   * UI-side gate mirroring the engine's "die satisfies an open cost slot" rule.
   * The engine is the authority (an illegal assign is caught and rejected in
   * `driveIntent`); this drives the drop-ready highlight and the click path.
   */
  private canAccept(card: Card, slot: DieSlot, state: CardCombatState): boolean {
    if (state.phase !== 'assign' || state.activeSide !== 'player') return false;
    if (slot.assignedTo !== null) return false;
    if (!isAssignable(card)) return false;
    // HEX-88 — a frozen card takes no dice (mirrors the engine throw). Keeps the
    // drag-over highlight honest (drop-reject, not drop-ready).
    if ((state.player.frozenCards.get(card.id) ?? 0) > 0) return false;
    const assignment = state.assignments.find((a) => a.cardId === card.id);
    const usedFaces = (assignment?.dieIndices ?? []).map((i) => state.pool[i]?.face);
    // §19 scaling: with `extraCost`, a card accepts dice past its cost slots (up
    // to the whole pool); cost is paid first, then extras match extraCost.predicate.
    const cap = card.extraCost ? state.pool.length : card.cost.length;
    if (usedFaces.length >= cap) return false;
    if (usedFaces.length < card.cost.length) {
      return openSlotAcceptsFace(card.cost, usedFaces, slot.face);
    }
    return card.extraCost !== undefined && matches(slot.face, card.extraCost.predicate);
  }

  // ── visual helpers ──────────────────────────────────────────────────────

  private startFrameLoop(): void {
    if (typeof requestAnimationFrame !== 'function') return;
    this.lastFrameMs = now();
    const frame = (): void => {
      const t = now();
      this.tweens.advance(t - this.lastFrameMs);
      this.lastFrameMs = t;
      this.rafHandle = requestAnimationFrame(frame);
    };
    this.rafHandle = requestAnimationFrame(frame);
  }

  /** Lerp each HP fill bar from its previous ratio to the new one (HEX-46
   * TweenRegistry). Width is set directly when no rAF is available (tests). */
  private animateHpBars(state: CardCombatState): void {
    for (const side of ['enemy', 'player'] as Side[]) {
      const combatant = side === 'enemy' ? state.enemy : state.player;
      const ratio = combatant.maxHp === 0 ? 0 : clamp01(combatant.hp / combatant.maxHp);
      const fill = this.root?.querySelector<HTMLElement>(`.hex6-card-combat__hpfill--${side}`);
      if (!fill) continue;
      const prev = this.prevHp.get(side);
      if (prev === undefined || this.rafHandle === null || prev === ratio) {
        fill.style.width = `${ratio * 100}%`;
      } else {
        const from = prev;
        this.tweens.start({
          durationMs: HP_TWEEN_MS,
          easing: easeOutQuad,
          onUpdate: (eased) => {
            fill.style.width = `${(from + (ratio - from) * eased) * 100}%`;
          },
        });
      }
      this.prevHp.set(side, ratio);
    }
  }

  private rejectDie(dieIndex: number): void {
    const node = this.root?.querySelector<HTMLElement>(`[${DATA_DIE_INDEX}="${dieIndex}"]`);
    if (!node) return;
    node.classList.remove(REJECT_CLASS);
    void node.offsetWidth;
    node.classList.add(REJECT_CLASS);
    const prev = this.rejectTimers.get(node);
    if (prev !== undefined) clearTimeout(prev);
    const handle = setTimeout(() => {
      node.classList.remove(REJECT_CLASS);
      this.rejectTimers.delete(node);
    }, REJECT_MS) as unknown as number;
    this.rejectTimers.set(node, handle);
  }
}

// ── pure helpers (module-local) ────────────────────────────────────────────

/** A card can take a die assignment when it has a non-empty cost (attack /
 * defense / support / recursive, plus loaded / charged traps fed dice).
 * Passive traps and die cards (empty cost) are not drop targets. */
export function isAssignable(card: Card): boolean {
  return card.cost.length > 0;
}

/** HEX-88 — build a fresh-roll log line with the rolled faces rendered as dice
 * glyphs (reusing `DIE_GLYPHS`) instead of bare numbers. A fizzle appends the
 * muted "fizzles — no effect" tail. Schematic — no new animation machinery. */
function renderRollFreshLine(
  line: HTMLElement,
  event: Extract<CombatEvent, { kind: 'rollFresh' }>,
  namer: SideNamer,
): void {
  line.appendChild(document.createTextNode(`› ${namer(event.side)} rolls fresh `));
  const dice = document.createElement('span');
  dice.className = 'hex6-card-combat__log-dice';
  for (const face of event.faces) {
    const die = document.createElement('span');
    die.className = 'hex6-card-combat__log-die';
    die.textContent = DIE_GLYPHS[face] ?? String(face);
    die.title = `face ${face}`;
    dice.appendChild(die);
  }
  line.appendChild(dice);
  if (event.fizzled) {
    const tail = document.createElement('span');
    tail.className = 'hex6-card-combat__log-fizzle';
    tail.textContent = ' — fizzles, no effect';
    line.appendChild(tail);
  }
}

/** A free, player-activated card (ADR-0015 §19; Reroll): a non-trap, non-die card
 * with no dice cost. It fires on click during the assign phase (capped by
 * `oncePerTurn`), not at turn resolution. */
export function isActivatable(card: Card): boolean {
  return card.type !== 'trap' && card.type !== 'die' && card.cost.length === 0;
}

/** Whether `face` fits some cost predicate not already satisfied by `usedFaces`.
 * Greedy: assigns each used face to the first matching unused predicate, then
 * checks the new face against a remaining one. Adequate for the UI gate; the
 * engine's backtracking matcher is the final authority. */
export function openSlotAcceptsFace(
  cost: readonly Predicate[],
  usedFaces: ReadonlyArray<DieFace | undefined>,
  face: DieFace,
): boolean {
  const consumed = new Array<boolean>(cost.length).fill(false);
  for (const used of usedFaces) {
    if (used === undefined) continue;
    const idx = cost.findIndex((p, i) => !consumed[i] && matches(used, p));
    if (idx >= 0) consumed[idx] = true;
  }
  return cost.some((p, i) => !consumed[i] && matches(face, p));
}

/** Map committed dice onto a card's cost slots for display (ADR-0021 §1). The
 * engine stores assigned dice as a flat list; the uniform combat face shows one
 * slot per cost predicate, so we recover which die sits in which slot. Returns
 * `slotFill` (a die pool-index or null per cost slot) and `extras` (dice beyond
 * the cost slots — §19 `extraCost` scaling). Uses a maximum bipartite matching
 * (Kuhn's) so a die that fits several predicates yields to one that fits fewer,
 * mirroring the engine's "every assigned die pays a distinct slot" guarantee. */
export function matchDiceToSlots(
  cost: readonly Predicate[],
  dice: ReadonlyArray<{ index: number; face: DieFace }>,
): { slotFill: Array<number | null>; extras: number[] } {
  const slotToDie = new Array<number>(cost.length).fill(-1); // slot → dice[] position
  const augment = (d: number, seen: boolean[]): boolean => {
    const die = dice[d];
    if (die === undefined) return false;
    for (let s = 0; s < cost.length; s++) {
      const predicate = cost[s];
      if (seen[s] || predicate === undefined) continue;
      if (!matches(die.face, predicate)) continue;
      seen[s] = true;
      if (slotToDie[s] === -1 || augment(slotToDie[s] as number, seen)) {
        slotToDie[s] = d;
        return true;
      }
    }
    return false;
  };
  for (let d = 0; d < dice.length; d++) augment(d, new Array<boolean>(cost.length).fill(false));
  const matched = new Set(slotToDie.filter((d) => d !== -1));
  const slotFill = slotToDie.map((d) => (d === -1 ? null : (dice[d]?.index ?? null)));
  const extras = dice.filter((_, d) => !matched.has(d)).map((die) => die.index);
  return { slotFill, extras };
}

/** A static face chip for an enemy card slot / extra during paced playback
 * (ADR-0021 §2) — the rolled die face, tagged by pool index so the flying cube can
 * find its landing target. Non-interactive (the enemy's dice aren't the player's). */
function buildEnemyFedChip(face: DieFace, dieIndex: number): HTMLSpanElement {
  const chip = document.createElement('span');
  chip.className = 'hex6-card-combat__fed-die';
  chip.setAttribute(DATA_DIE_INDEX, String(dieIndex));
  chip.textContent = DIE_GLYPHS[face] ?? String(face);
  return chip;
}

/** One enemy card's reconstructed die feed (ADR-0021 §2). The engine clears the
 * active side's `assignments` at turn-end, so the enemy turn's die→card mapping is
 * gone by the time the overlay holds the resolved state. We rebuild it — pure
 * presentation over the same events — from the enemy's rolled pool + the ordered
 * `cardPlayed` log: each played card claims dice satisfying its cost slots, then
 * its `extraCost` scaling, from what's left. `slots[i]` is the pool-die index in
 * cost slot `i` (null if the remaining pool couldn't satisfy it); `extras` are the
 * scaling dice past the cost. The mapping is self-consistent (each die feeds at
 * most one card) — which specific die of equal faces the bot picked is invisible. */
export interface EnemyPlay {
  cardId: string;
  slots: Array<number | null>;
  extras: number[];
}

/** Rebuild the enemy turn's die→card assignment for animation (ADR-0021 §2).
 * `pool` is the enemy's rolled dice (its `index` is the pool position); `playedCards`
 * are the enemy loadout cards in `cardPlayed` order. Cost slots are filled first
 * (maximum matching, reusing {@link matchDiceToSlots}); leftover dice then feed the
 * `extraCost` cards in play order. Pure — no DOM, no engine call. */
export function reconstructEnemyPlays(
  pool: ReadonlyArray<{ index: number; face: DieFace }>,
  playedCards: ReadonlyArray<Card>,
): EnemyPlay[] {
  const used = new Set<number>();
  const plays: EnemyPlay[] = playedCards.map((card) => {
    const unused = pool.filter((d) => !used.has(d.index));
    const { slotFill } = matchDiceToSlots(card.cost, unused);
    for (const idx of slotFill) if (idx !== null) used.add(idx);
    return { cardId: card.id, slots: slotFill, extras: [] };
  });
  // Second pass: the dice still unspent feed the played cards that scale off extra
  // dice (§19 `extraCost`), in play order, matching the scaling predicate.
  playedCards.forEach((card, p) => {
    const extraCost = card.extraCost;
    const play = plays[p];
    if (!extraCost || !play) return;
    for (const d of pool) {
      if (used.has(d.index)) continue;
      if (matches(d.face, extraCost.predicate)) {
        used.add(d.index);
        play.extras.push(d.index);
      }
    }
  });
  return plays;
}

/** Assemble the enemy-turn animation plan from the pre-resolution state, the
 * resolved state, and the narrated event tail (ADR-0021 §2). `prev` is the
 * enemy-turn-start state — its active side is the enemy, so `prev.pool` is the
 * enemy's freshly rolled dice (`rollForActive` numbers each die by pool position).
 * `playEventIndex[p]` is the tail index whose reveal lands play `p`'s dice. */
function buildEnemyAnim(
  prev: CardCombatState,
  next: CardCombatState,
  events: readonly CombatEvent[],
): { pool: Array<{ index: number; face: DieFace }>; plays: EnemyPlay[]; playEventIndex: number[] } {
  const pool = prev.pool.map((d) => ({ index: d.index, face: d.face }));
  const playedCards: Card[] = [];
  const playEventIndex: number[] = [];
  events.forEach((event, i) => {
    if (event.kind !== 'cardPlayed' || event.side !== 'enemy') return;
    const card = next.enemy.inventory.loadout.placements.find(
      (p) => p.card.id === event.cardId,
    )?.card;
    if (card) {
      playedCards.push(card);
      playEventIndex.push(i);
    }
  });
  return { pool, plays: reconstructEnemyPlays(pool, playedCards), playEventIndex };
}

function readDragDieIndex(e: DragEvent): number | null {
  const dt = e.dataTransfer;
  if (!dt) return null;
  let raw = '';
  try {
    raw = dt.getData(DRAG_MIME);
  } catch {
    raw = '';
  }
  if (raw === '') {
    try {
      raw = dt.getData('text/plain');
    } catch {
      raw = '';
    }
  }
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** The side an event's feedback plays on (ADR-0017 §B): the struck / afflicted
 * combatant for the moment-bearing kinds; the acting side otherwise (those kinds
 * are no-ops in `playJuice`, so the fallback is never actually rendered). */
function juiceSubject(event: CombatEvent, acting: Side): Side {
  switch (event.kind) {
    case 'damage':
    case 'statusApplied':
      return event.target;
    case 'poisonTick':
    case 'block':
    case 'rollFresh': // HEX-88 — fizzle cue plays on the side that rolled fresh
      return event.side;
    default:
      return acting;
  }
}

/** Honour the OS "reduce motion" setting: dice show their face instantly with no
 * tumble (ADR-0017 §4). Guarded for environments without `matchMedia` (tests). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
